import React, {
	useEffect, useState, useRef, useMemo, useContext,
	useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Shot, ShotFeed } from '../../stores/types/shots';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import Dialog from '../general-components/dialog';
import * as SessionActions from '../../stores/actions/session';
import * as NotesActions from '../../stores/actions/notes';
import * as NotificationActions from '../../stores/actions/notifications';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { BROADCAST_STATES } from '../../stores/types/broadcasting';
import { NOTIFICATION_STYLES } from '../../stores/reducers/notifications';
import Avatar from '../general-components/avatar';
import NotificationsView from '../main-video/notifications-view';
import useShots from '../../contexts/shots/provider';
import { getChannelLogoFromMeeting, logOut } from '../../utils/utils';
import { GreenroomState as GS } from '../../stores/types/types';
import type { HomeNotification } from '../../stores/types/home';
import { NOTIFICATION_TYPES } from '../../stores/reducers/home';
import * as Alerts from '../general-components/alert-service';
import { LoadNotifications } from '../../connection/home';
import TellyChannelLogo from '../../images/app-store-icon.png';
import BroadcastControls from '../broadcasting/broadcast-controls';
import StreamBadge from '../broadcasting/stream-badge';
import HeaderLeftControls from '../header-components/header-left-controls';
import ScheduledStreamCommsModal from '../broadcasting/scheduled-stream-comms-modal';
import { selectOpentokSession, selectPlatformRedirectUrl } from '../../stores/selectors/session';
import { closeMetaMenu, setActiveTab, setMetaMenuOption, setMetaMenuRoute, setMetaMenuState, setRightDrawerOpen } from '../../stores/actions/tabs';
import { META_MENU_ROUTES, META_MENU_STATES, MetaMenuOptions, TABS } from '../../stores/types/tabs';
import { SocketContext } from '../../contexts/socket';
import HeaderDrawerButtons from './header/header-drawer-buttons';
import HeaderClockContainer from './header/clock-container';
import classNames from 'classnames';

const avatarHighlightingRoutes = [
	META_MENU_ROUTES.PROFILE,
	META_MENU_ROUTES.PRESENTER_SETTINGS,
	META_MENU_ROUTES.EFFECTS,
	META_MENU_ROUTES.ALL_NOTES,
];

export default function Header() {
	const dispatch = useDispatch();
	const {socket} = useContext(SocketContext);
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const avatarPopoverOpen = useSelector((s: GS) => s.SessionReducer.avatarPopoverOpen);
	const watcherOnly = useSelector((s: GS) => s.SessionReducer.watcher_only);
	const companionMode = useSelector((s: GS) => s.SessionReducer.companionMode);
	const {activeTab, metaMenuState, metaMenuActiveRoute} = useSelector((s: GS) => s.TabsReducer);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const notesDrawerOpen = useSelector((s: GS) => s.NotesReducer.notesDrawerOpen);
	const shots = useSelector((s: GS) => s.ShotsReducer.shots);
	const serverState = useSelector((s: GS) => s.BroadcastReducer.serverState);
	const showDebugUI = useSelector((s: GS) => s.OpenTokReducer.showDebugUI);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const helpMenuOpen = useSelector((s: GS) => s.SessionReducer.helpMenuOpen);
	const scheduledStream = useSelector((s: GS) => s.MeetingReducer.scheduledStream);
	const opentokSession = useSelector(selectOpentokSession);
	const platformRedirectUrl = useSelector(selectPlatformRedirectUrl);
	const shotStartTimestampEntity = useSelector(
		(s: GS) => s.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);

	const [dialogRight, setDialogRight] = useState<boolean>(false);
	const [scheduledStreamCommModalOpen, setScheduledStreamCommModalOpen] = useState<boolean>(false);
	const ignoredCommsModal = useRef<boolean>(false);

	const dialog = useRef<any>(null);

	const { currentShot } = useShots();

	const {
		shot_id: shotId,
		shot_data: {
			shotFeeds,
			shotDuration,
			autoDuration,
		},
	} = currentShot;

	const setIgnoredCommsModal = (ignored: boolean) => {
		ignoredCommsModal.current = ignored;
	};

	const getUrlForBackButton = (): string => {
		const { origin, pathname } = window.location;
		const producerString = '/producer';
		const isProducerUrl = pathname.startsWith(producerString);

		return `${origin}${isProducerUrl ? producerString : ''}`;
	};

	// rather than go back in redux and have to manage disconnecting all the
	// websockets and webrtc connection, just reload
	// this is not as smooth but is basically foolproof
	// eslint-disable-next-line @typescript-eslint/no-shadow
	const leave = useCallback(() => {
		setDialogRight(dialogRight);
		dialog.current.confirm(
			'Are you sure you want to leave this greenroom?',
			null,
			'Exit',
			'Cancel',
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			(confirmed: boolean, cancelled: boolean) => {
				if (confirmed) {
					window.location.href = (host && platformRedirectUrl) ? platformRedirectUrl : getUrlForBackButton();
				}
			},
		);
	}, [platformRedirectUrl, host]);

	function confirmSignout() {
		setDialogRight(true);
		dialog.current?.confirm(
			'Are you sure you want to sign out?',
			'You will be taken back to the sign in form.',
			'Sign Out',
			'Cancel',
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			(confirmed: boolean, cancelled: boolean) => {
				if (confirmed) {
					logOut(host);
				}
			},
		);
	}

	const toggleDrawer = (metaMenuRoute: string) => {
		if (metaMenuState === META_MENU_STATES.CLOSED) {
			dispatch(setMetaMenuRoute(metaMenuRoute));
			dispatch(setMetaMenuState(META_MENU_STATES.OPENING));
		} else if (metaMenuState === META_MENU_STATES.OPEN) {
			if (metaMenuRoute !== metaMenuActiveRoute) {
				dispatch(setMetaMenuRoute(metaMenuRoute));
			} else {
				dispatch(closeMetaMenu());
			}
		}
	};

	const userIsInCanvas = useMemo(
		() => shotFeeds.some((feed: ShotFeed) => feed.presenter === user!.gr_user),
		[shotFeeds, user],
	);

	const userIsInNextShot = useMemo(() => {
		// eslint-disable-next-line camelcase
		const currentShotIndex = shots.findIndex((shot: Shot) => shot.shot_id === shotId);
		const nextShot = shots[currentShotIndex + 1];
		if (
			nextShot
			&& nextShot.shot_data.shotFeeds.some(
				(feed: ShotFeed) => feed.presenter === user!.gr_user,
			)
		) {
			return true;
		}
		return false;
	// eslint-disable-next-line camelcase
	}, [shots, shotId, user]);

	useEffect(() => {
		if (userIsInNextShot && shotStartTimestampEntity) {
			dispatch(NotificationActions.newNotifcation({
				type: NOTIFICATION_STYLES.TIMED,
				title: 'Get ready to go live',
				subtitle: 'Don\'t forget to look at the camera',
				icon: ICONS.ARROW_UP_FULL,
			}));
		}
		if (!userIsInNextShot || userIsInCanvas || roomState === ROOM_STATES.EDITING) {
			dispatch(NotificationActions.removeNotification());
		}
	}, [userIsInNextShot, userIsInCanvas, shotStartTimestampEntity, roomState]);

	useEffect(() => {
		const checkChatNotifications = async () => {
			if (meeting && token) {
				const notifications = await LoadNotifications(token);

				// check if user has any unread message notifications and send toaster
				// eslint-disable-next-line @typescript-eslint/no-shadow
				if (notifications.some((notification: HomeNotification) => notification.notification
					&& notification.meeting === meeting.meeting
					&& !notification.has_been_seen
					// eslint-disable-next-line eqeqeq
					&& (notification.type == NOTIFICATION_TYPES.COMMENT_BASIC
						// eslint-disable-next-line eqeqeq
						|| notification.type == NOTIFICATION_TYPES.COMMENT_MENTION))) {
					Alerts.showAlert({
						message: 'Unread Chat',
						description: 'You have unread messages from one or more chat rooms or direct messages from when you last joined Greenroom.',
						onClick: () => {
							dispatch(setActiveTab(TABS.CHAT));
							dispatch(setRightDrawerOpen(true));
						},
						duration: 10000,
					});
				}
			}
		};

		checkChatNotifications();
	}, []);

	const channelLogo: string = useMemo(() => (
		getChannelLogoFromMeeting(meeting) || TellyChannelLogo
	), [meeting]);

	const showNotificationsView: boolean = useMemo(
		() => {
			const isCountingDown = shotDuration || autoDuration || (userIsInNextShot && !userIsInCanvas);
			const hasNotification = shotStartTimestampEntity && (userIsInNextShot || userIsInCanvas);
			return isCountingDown && hasNotification;
		},
		[
			shotDuration,
			autoDuration,
			userIsInNextShot,
			userIsInCanvas,
			shotStartTimestampEntity,
		],
	);

	const avatarIsHighlighted = useMemo(() => activeTab === TABS.USER
			|| avatarPopoverOpen
			|| notesDrawerOpen
			|| (
				avatarHighlightingRoutes.includes(metaMenuActiveRoute)
				&& metaMenuState !== META_MENU_STATES.CLOSED
			), [
		activeTab,
		avatarPopoverOpen,
		notesDrawerOpen,
		metaMenuState,
		metaMenuActiveRoute,
	]);

	const stylepacksOpen = useMemo(() => {
		return metaMenuState !== META_MENU_STATES.CLOSED && (
			metaMenuActiveRoute === META_MENU_ROUTES.STYLE_PACKS
			|| metaMenuActiveRoute === META_MENU_ROUTES.STYLE_PACK_INFO
		);
	}, [metaMenuState, metaMenuActiveRoute]);

	return (
		<header className="event-header">
			<div id="mode-line-portal" />
			<div className='header-left'>
				<HeaderLeftControls
					channelLogo={channelLogo}
					title={meeting?.title || ''}
					backButtonHandler={leave}
				/>
				{scheduledStream&& (
					<StreamBadge startTime={scheduledStream.startTime} host={host} socket={socket} />
				)}
			</div>

			{showNotificationsView && <div className="header-center">
				<NotificationsView
					key={shotId}
					socket={socket}
					userIsInCanvas={userIsInCanvas}
					userIsInNextShot={userIsInNextShot}
				/>
			</div>}

			<div className={classNames('header-right', {'stylepacks-drawer-open': stylepacksOpen})}>
				<HeaderClockContainer 
					scheduledStartTime={scheduledStream?.startTime}
					meetingSlug={meeting?.meeting_id}
					meetingStartTime={meeting?.start_time}
					host={host}
					roomState={roomState}
					serverState={serverState}
				/>

				<BroadcastControls socket={socket} />

				<Dialog ref={dialog} right={dialogRight} />

				{host && <div
					className='header-dividing-line margin-left-small stylepacks-do-fade'
					style={{ marginLeft: 16, marginRight: 8 }}
				/>}

				<HeaderDrawerButtons 
					host={host}
					helpMenuOpen={helpMenuOpen}
					showbuilder={roomState === ROOM_STATES.EDITING}
				/>

				<div
					className='header-dividing-line margin-left-small stylepacks-do-fade'
					style={{ marginLeft: 8, marginRight: 4 }}
				/>

				<div className='avatar-button stylepacks-do-fade' onClick={() => dispatch(SessionActions.setAvatarPopoverOpen(!avatarPopoverOpen))}>
					<Avatar
						user={user!}
						size="medium"
						noBorder={true}
						host={host}
						inMeeting={true}
						selected={avatarIsHighlighted}
						setAvatarPopoverOpen={(isOpen: boolean) => (
							dispatch(SessionActions.setAvatarPopoverOpen(isOpen))
						)}
						avatarPopoverOpen={avatarPopoverOpen}
					>
						<div className="select-options field-group select">
							<li className="select-option" onClick={() => {
								toggleDrawer(META_MENU_ROUTES.PROFILE);
								dispatch(SessionActions.setAvatarPopoverOpen(false));
							}}>
								<Icon name={ICONS.PRESENTERS} color={COLORS.TEXT_GRAY_100} size={13} />Profile
							</li>
							{
								!watcherOnly && !companionMode && (
									<li className="select-option" onClick={() => {
										toggleDrawer(META_MENU_ROUTES.PRESENTER_SETTINGS);
										dispatch(SessionActions.setAvatarPopoverOpen(false));
									}}>
										<Icon name={ICONS.MICROPHONE_ON} color={COLORS.TEXT_GRAY_100} size={13} />Devices
									</li>
								)
							}
							{
								!watcherOnly && !companionMode && (
									<li className="select-option" onClick={() => {
										toggleDrawer(META_MENU_ROUTES.EFFECTS);
										dispatch(SessionActions.setAvatarPopoverOpen(false));
									}}>
										<Icon name={ICONS.SPARKLE} color={COLORS.TEXT_GRAY_100} size={13} />Effects
									</li>
								)
							}
							<li className="select-option" onClick={() => {
								dispatch(NotesActions.setShowWorkingAllNote(false));
								toggleDrawer(META_MENU_ROUTES.ALL_NOTES);
							}}>
								<Icon name={ICONS.NOTE} color={COLORS.TEXT_GRAY_100} size={13} />All Notes
							</li>
							<li className="select-option" onClick={() => {
								confirmSignout();
								dispatch(SessionActions.setAvatarPopoverOpen(false));
							}}>
								<Icon name={ICONS.LOG_OUT} color={COLORS.TEXT_GRAY_100} size={13} />Sign Out
							</li>
						</div>
					</Avatar>
				</div>

				{showDebugUI && <span className="debug-opentok-session">{opentokSession}</span>}
				{scheduledStreamCommModalOpen && (
					<ScheduledStreamCommsModal
						isLive={roomState === ROOM_STATES.SHOWTIME && serverState === BROADCAST_STATES.BROADCAST_STARTED}
						goToSettings={() => {
							setIgnoredCommsModal(true);
							setScheduledStreamCommModalOpen(false);
							dispatch(setMetaMenuState(META_MENU_STATES.OPENING));
							dispatch(setMetaMenuRoute(META_MENU_ROUTES.EVENT_SETTINGS));
							dispatch(setMetaMenuOption(MetaMenuOptions.Settings));
						}}
						handleClick={() => {
							setIgnoredCommsModal(true);
							setScheduledStreamCommModalOpen(false);
						}}
					/>
				)}
			</div>
		</header>
	);
}
